import LFCBaseCharts from '_components/charts/base/LFCBaseCharts';
import {fmtThreeCommas, fmtYYYYMMDDHHmmss, useFormatWithSettings} from '_logics/LFCFormatUtil';
import {useParseWithSettings} from '_logics/LFCParseUtil';
import {isEmpty} from 'lodash';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {green, purple, red, yellow} from '@mui/material/colors';

import {calculatePercent} from '../logics';
import {SearchType, SeriesType} from '../types';

interface SeriesData {
  value: number; // par
  serial: string;
  uuid: string;
  lq_time: string;
  map: string;
  sum_point: number;
  sum_allpoint: number;
  sum_okpoint: number;
  sum_ngpoint: number;
  sum_warningpoint: number;
  sum_overpoint: number;
  sum_okpar: number;
  sum_ngpar: number;
  sum_warningpar: number;
  sum_overpar: number;
}

interface Series {
  name: SeriesType.OK | SeriesType.NG | SeriesType.WARNING | SeriesType.OVER;
  type: string;
  stack: string;
  color: string;
  data: SeriesData[];
}

const DISPLAY_INFO: Record<SeriesType, Record<string, any>> = {
  [SeriesType.NG]: {name: 'NG', color: red[500]},
  [SeriesType.WARNING]: {name: 'WARNING', color: yellow[500]},
  [SeriesType.OK]: {name: 'OK', color: green[500]},
  [SeriesType.OVER]: {name: 'OVER', color: purple[500]}
};

/**
 * 引数
 */
interface MatchingRateChartProps {
  title: string;
  source: Record<string, any>[];
  xlist: string[];
  searchType: SearchType;
  exportFilename?: string;
  height?: string;
  zoomChartId?: string;
  zoomedChartId?: string;
  zoomCharts?: (zoomChartId?: string) => void;
  zoomChartsClose?: (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void;

  onClick?: (prm: {data: any}) => void;
}

/**
 * MatchingRateChart
 * @param props
 * @returns
 */
export const MatchingRateChart = (props: MatchingRateChartProps) => {
  const {t} = useTranslation();
  const {fmtPercent} = useFormatWithSettings();
  const {parsePercent} = useParseWithSettings();
  const [series, setSeries] = useState<Series[]>([]);
  const [exportFields, setExportFields] = useState<Record<string, any>>({});
  const [exportData, setExportData] = useState<Record<string, any>[]>([]);
  const [xAxisData, setXAxisData] = useState<string[]>([]);

  useEffect(() => {
    if (isEmpty(props.source)) return;

    //X軸のリストを生成
    const xlists: string[] =
      props.searchType === SearchType.WORK
        ? Array.from(new Set(props.source.map((a: any) => a.x_timeline)))
        : props.xlist;
    setXAxisData(xlists);

    //ブランチを分けないで部位毎で集約する
    const datas: Record<string, any>[] = [];
    xlists.forEach((xl: string) => {
      const filteredRows = props.source.filter((r: any) => r.x_timeline === xl);
      const sums = filteredRows.reduce(
        (acc, r) => {
          return {
            ...acc,
            sum_allpoint: acc.sum_allpoint + Number(r.sum_allpoint),
            [SeriesType.OK]: acc[SeriesType.OK] + Number(r[SeriesType.OK]),
            [SeriesType.NG]: acc[SeriesType.NG] + Number(r[SeriesType.NG]),
            [SeriesType.WARNING]: acc[SeriesType.WARNING] + Number(r[SeriesType.WARNING]),
            [SeriesType.OVER]: acc[SeriesType.OVER] + Number(r[SeriesType.OVER])
          };
        },
        {
          serial: filteredRows[0]?.serial,
          uuid: filteredRows[0]?.uuid,
          lq_time: filteredRows[0]?.lq_time,
          map: filteredRows[0]?.map,
          sum_allpoint: 0,
          [SeriesType.OK]: 0,
          [SeriesType.NG]: 0,
          [SeriesType.WARNING]: 0,
          [SeriesType.OVER]: 0
        }
      );

      datas.push(sums);
    });

    const tmpSeries: Series[] = [];

    Object.entries(DISPLAY_INFO).forEach(([key, displayInfo]) => {
      tmpSeries.push({
        name: displayInfo.name,
        type: 'bar',
        stack: 'total',
        color: displayInfo.color,
        data: datas.map((data: any) => {
          const perInfo: Record<string, number> = {
            [SeriesType.OK]: parsePercent(calculatePercent(data[SeriesType.OK], data.sum_allpoint)),
            [SeriesType.NG]: parsePercent(calculatePercent(data[SeriesType.NG], data.sum_allpoint)),
            [SeriesType.WARNING]: parsePercent(
              calculatePercent(data[SeriesType.WARNING], data.sum_allpoint)
            ),
            [SeriesType.OVER]: parsePercent(
              calculatePercent(data[SeriesType.OVER], data.sum_allpoint)
            )
          };

          return {
            value: perInfo[key],
            serial: data.serial,
            uuid: data.uuid,
            lq_time: data.lq_time,
            map: data.map,
            sum_point: data[key],
            sum_allpoint: data.sum_allpoint,
            sum_okpoint: data[SeriesType.OK],
            sum_ngpoint: data[SeriesType.NG],
            sum_warningpoint: data[SeriesType.WARNING],
            sum_overpoint: data[SeriesType.OVER],
            sum_okpar: perInfo[SeriesType.OK],
            sum_ngpar: perInfo[SeriesType.NG],
            sum_warningpar: perInfo[SeriesType.WARNING],
            sum_overpar: perInfo[SeriesType.OVER]
          };
        })
      });
    });
    setSeries(tmpSeries);
  }, [props.source, props.xlist]);

  useEffect(() => {
    if (props.searchType === SearchType.WORK) {
      //検査ごと
      setExportFields({
        検査日時: t('検査日時'),
        シリアルナンバー: t('シリアルナンバー'),
        検査部位: t('検査部位'),
        合計点数: t('合計点数'),
        ＮＧ比率: t('ＮＧ比率(%)'),
        ＮＧ点数: t('ＮＧ点数'),
        ＷＡＲＮＩＮＧ比率: t('ＷＡＲＮＩＮＧ比率(%)'),
        ＷＡＲＮＩＮＧ点数: t('ＷＡＲＮＩＮＧ点数'),
        ＯＫ比率: t('ＯＫ比率(%)'),
        ＯＫ点数: t('ＯＫ点数'),
        ＯＶＥＲ比率: t('ＯＶＥＲ比率(%)'),
        ＯＶＥＲ点数: t('ＯＶＥＲ点数')
      });
    } else {
      setExportFields({
        検査日時: t('検査日時'),
        検査部位: t('検査部位'),
        合計点数: t('合計点数'),
        ＮＧ比率: t('ＮＧ比率(%)'),
        ＮＧ点数: t('ＮＧ点数'),
        ＷＡＲＮＩＮＧ比率: t('ＷＡＲＮＩＮＧ比率(%)'),
        ＷＡＲＮＩＮＧ点数: t('ＷＡＲＮＩＮＧ点数'),
        ＯＫ比率: t('ＯＫ比率(%)'),
        ＯＫ点数: t('ＯＫ点数'),
        ＯＶＥＲ比率: t('ＯＶＥＲ比率(%)'),
        ＯＶＥＲ点数: t('ＯＶＥＲ点数')
      });
    }
  }, [props.searchType]);

  useEffect(() => {
    if (series.length === 0) return;

    const rowSeriesData = series.find(s => s.name === DISPLAY_INFO[SeriesType.OK].name)?.data;

    if (!rowSeriesData) return; //空行はスキップ

    if (props.searchType === SearchType.WORK) {
      //検査ごと

      const _exportData = xAxisData
        .map((xl, i) => {
          if (rowSeriesData[i].sum_allpoint === 0) return null; //合計点数0はデータなしなのでスキップ

          return {
            検査日時: fmtYYYYMMDDHHmmss(rowSeriesData[i].lq_time),
            シリアルナンバー: rowSeriesData[i].serial || rowSeriesData[i].uuid,
            検査部位: rowSeriesData[i].map,
            合計点数: rowSeriesData[i].sum_allpoint,
            ＮＧ比率: rowSeriesData[i].sum_ngpar,
            ＮＧ点数: rowSeriesData[i].sum_ngpoint,
            ＷＡＲＮＩＮＧ比率: rowSeriesData[i].sum_warningpar,
            ＷＡＲＮＩＮＧ点数: rowSeriesData[i].sum_warningpoint,
            ＯＫ比率: rowSeriesData[i].sum_okpar,
            ＯＫ点数: rowSeriesData[i].sum_okpoint,
            ＯＶＥＲ比率: rowSeriesData[i].sum_overpar,
            ＯＶＥＲ点数: rowSeriesData[i].sum_overpoint
          };
        })
        .filter(r => r !== null);

      setExportData(_exportData as Record<string, any>[]);
    } else {
      const _exportData = xAxisData
        .map((xl, i) => {
          if (rowSeriesData[i].sum_allpoint === 0) return null; //合計点数0はデータなしなのでスキップ

          return {
            検査日時: xl,
            検査部位: rowSeriesData[i].map,
            合計点数: rowSeriesData[i].sum_allpoint,
            ＮＧ比率: rowSeriesData[i].sum_ngpar,
            ＮＧ点数: rowSeriesData[i].sum_ngpoint,
            ＷＡＲＮＩＮＧ比率: rowSeriesData[i].sum_warningpar,
            ＷＡＲＮＩＮＧ点数: rowSeriesData[i].sum_warningpoint,
            ＯＫ比率: rowSeriesData[i].sum_okpar,
            ＯＫ点数: rowSeriesData[i].sum_okpoint,
            ＯＶＥＲ比率: rowSeriesData[i].sum_overpar,
            ＯＶＥＲ点数: rowSeriesData[i].sum_overpoint
          };
        })
        .filter(r => r !== null);

      setExportData(_exportData as Record<string, any>[]);
    }
  }, [xAxisData, series, props.searchType]);

  return (
    <LFCBaseCharts
      option={{
        title: {
          text: props.title
        },
        grid: {
          top: 60,
          bottom: 60,
          right: 5,
          left: 60
        },
        legend: {
          type: 'scroll',
          bottom: 0
        },
        tooltip: {
          show: true,
          formatter: (params: any) => {
            const dinfo = Object.values(DISPLAY_INFO).find(r => r.name === params.seriesName);
            const borderColor = dinfo?.color;
            const icon = borderColor ? `<span style="color:${borderColor}">●</span> ` : '';

            return `${xAxisData[params.dataIndex]}<br>
              ${icon}${params.seriesName}<br/>
              ${fmtPercent(params.value)} (${fmtThreeCommas(params.data.sum_point)}点)<br/>
              <br/>
              ${
                props.searchType === SearchType.WORK
                  ? `<span style="font-weight:bold">${t('シリアルナンバー')}</span>: ${
                      params.data.serial || params.data.uuid
                    }<br/>`
                  : ''
              }
              <span style="font-weight:bold">${t('合計点群')}</span>: ${fmtThreeCommas(
                params.data.sum_allpoint
              )}点<br/>
              &nbsp;&nbsp;<span style="font-weight:bold">${t('NG')}</span>: ${fmtPercent(
                params.data.sum_ngpar
              )} (${fmtThreeCommas(params.data.sum_ngpoint)}点)<br/>
              &nbsp;&nbsp;<span style="font-weight:bold">${t('WARNING')}</span>: ${fmtPercent(
                params.data.sum_warningpar
              )} (${fmtThreeCommas(params.data.sum_warningpoint)}点)<br/>
              &nbsp;&nbsp;<span style="font-weight:bold">${t('OK')}</span>: ${fmtPercent(
                params.data.sum_okpar
              )} (${fmtThreeCommas(params.data.sum_okpoint)}点)<br/>
              &nbsp;&nbsp;<span style="font-weight:bold">${t('OVER')}</span>: ${fmtPercent(
                params.data.sum_overpar
              )} (${fmtThreeCommas(params.data.sum_overpoint)}点)<br/>
              `;
          }
        },
        dataZoom: [
          {
            type: 'inside'
          }
        ],

        xAxis: {
          type: 'category',
          name: '',
          data: xAxisData,
          axisLabel: {
            rotate: '20',
            fontSize: 10
          }
        },
        yAxis: {
          type: 'value',
          name: '(%)',
          max: null,
          scale: true
        },
        series: series
      }}
      exportData={exportData}
      exportFields={exportFields}
      exportFilename={props.exportFilename}
      height={props.height ? props.height : '100px'}
      onEvents={{
        click: (params: any) => {
          if (props.onClick != null) {
            props.onClick(params);
          }
        }
      }}
      zoomCharts={props.zoomCharts ? props.zoomCharts : undefined}
      zoomChartsClose={props.zoomChartsClose ? props.zoomChartsClose : undefined}
      zoomChartId={props.zoomChartId ? props.zoomChartId : undefined}
      zoomStatus={props.zoomChartId === props.zoomedChartId}
    />
  );
};
